import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogOut, LayoutDashboard, ShieldCheck, GraduationCap, Users, ClipboardCheck, UserRound, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { avatarUrl } from "@/lib/avatar";

// Desktop avatar menu: Dashboard / Profile / role tools / Sign out, so the bar stays uncluttered.
export default function AccountMenu({ user, billing, onLogout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const staff = user.role === "instructor" || user.role === "administrator";
  const items = [
    { key: "dashboard", to: "/dashboard", icon: LayoutDashboard, label: t("nav.dashboard") },
    { key: "profile", to: "/profile", icon: UserRound, label: t("nav.profile") },
    staff && { key: "instructor", to: "/instructor", icon: GraduationCap, label: t("nav.instructor"), accent: true },
    (billing.plan === "company" || billing.source === "role") && { key: "inspections", to: "/inspections", icon: ClipboardCheck, label: t("nav.inspections", "Inspections") },
    billing.team_access && { key: "team", to: "/team", icon: Users, label: t("nav.team") },
    user.role === "administrator" && { key: "admin", to: "/admin", icon: ShieldCheck, label: t("nav.admin"), accent: true },
  ].filter(Boolean);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid="nav-account-menu" aria-label={t("nav.account", "Account menu")}
          className="inline-flex items-center gap-1.5 h-9 pl-1 pr-2 rounded-full border border-white/10 hover:bg-white/10 transition-colors duration-150">
          {user.picture ? (
            <img src={avatarUrl(user.picture)} alt="" referrerPolicy="no-referrer" data-testid="nav-avatar" className="w-7 h-7 rounded-full object-cover border border-white/20" />
          ) : (
            <span data-testid="nav-avatar" className="grid place-items-center w-7 h-7 rounded-full bg-primary/20 text-primary text-xs font-bold">{(user.name || "?").charAt(0).toUpperCase()}</span>
          )}
          <ChevronDown size={14} className="text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]" data-testid="nav-account-dropdown">
        <DropdownMenuLabel className="font-normal">
          <span className="block text-sm font-semibold text-white truncate">{user.name || user.email}</span>
          <span className="block text-xs text-slate-400 truncate">{user.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.map((it) => (
          <DropdownMenuItem key={it.key} data-testid={`nav-${it.key}-btn`} onSelect={() => navigate(it.to)} className={`cursor-pointer gap-2 ${it.accent ? "text-primary focus:text-primary" : ""}`}>
            <it.icon size={15} /> {it.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="nav-logout-btn" onSelect={onLogout} className="cursor-pointer gap-2 text-slate-300">
          <LogOut size={15} /> {t("nav.signOut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
